// Intent / use-case marketing pages (D-44). SSR-safe: MarketingLayout, <a href>, no Auth.

import { CtaBand } from "../components/marketing/CtaBand.js";
import { FaqItem } from "../components/marketing/FaqItem.js";
import { MarketingLayout } from "../components/marketing/MarketingLayout.js";
import { Section } from "../components/marketing/Section.js";
import { useCaseByPath, type UseCaseContent } from "../lib/useCaseContent.js";
import { useSignupMode } from "../hooks/useSignupMode.js";
import { signupLabel, signupBandBody } from "../lib/signupCopy.js";

export function UseCasePage({ content }: { content: UseCaseContent }) {
  const { mode: signupMode } = useSignupMode();
  return (
    <MarketingLayout>
      <Section tone="surface">
        <h1 className="text-3xl font-semibold tracking-tight text-ink sm:text-5xl">{content.h1}</h1>
        <p className="mt-5 max-w-[65ch] text-lg leading-8 text-muted">{content.lead}</p>
      </Section>

      {content.sections.map((section, i) => (
        <Section key={section.heading} tone={i % 2 === 0 ? "slate" : "surface"}>
          <h2 className="text-2xl font-semibold text-ink sm:text-3xl">{section.heading}</h2>
          <p className="mt-4 max-w-[65ch] text-base leading-7 text-muted">{section.body}</p>
          {section.heading === "Start from a published list" ? (
            <p className="mt-4">
              <a
                href="/community"
                className="text-base font-semibold text-brand-700 no-underline hover:text-brand-600"
              >
                Browse community lists →
              </a>
            </p>
          ) : null}
        </Section>
      ))}

      <Section tone="surface">
        <h2 className="text-2xl font-semibold text-ink sm:text-3xl">{content.example.heading}</h2>
        <ul className="mt-6 max-w-md list-disc space-y-2 pl-5 text-base leading-7 text-ink">
          {content.example.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </Section>

      <Section tone="slate">
        <h2 className="text-2xl font-semibold text-ink sm:text-3xl">Questions</h2>
        <div className="mx-auto mt-8 max-w-2xl space-y-3">
          {content.faqs.map((faq) => (
            <FaqItem key={faq.q} question={faq.q} answer={faq.a} />
          ))}
        </div>
      </Section>

      <Section tone="surface">
        <CtaBand
          title="Start with a fresh checklist"
          body={signupBandBody(signupMode)}
          primaryHref="/register"
          primaryLabel={signupLabel("nav", signupMode)}
          secondaryHref="/community"
          secondaryLabel="Browse community lists"
        />
      </Section>
    </MarketingLayout>
  );
}

function page(path: string) {
  const content = useCaseByPath(path);
  if (!content) throw new Error(`Missing use-case content for ${path}`);
  return <UseCasePage content={content} />;
}

export function PackingListPage() {
  return page("/packing-list");
}

export function SharedChecklistPage() {
  return page("/shared-checklist");
}

export function WeddingChecklistPage() {
  return page("/wedding-checklist");
}

export function CampingChecklistPage() {
  return page("/camping-checklist");
}

export function OnboardingChecklistPage() {
  return page("/onboarding-checklist");
}
